import React, { useEffect, useState, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  VStack,
  Heading,
  Text,
  Box,
  Button,
  HStack,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon
} from '@chakra-ui/react';

import { auth, db, getUsers } from '../api';
import { collection, query, where, getDocs } from 'firebase/firestore';
import CustomDatePicker from '../components/CustomDatePicker';
import Footer from '../components/Footer';

function CashierMonitor() {
  const [user, loadingAuth] = useAuthState(auth);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState('');

  const [from, setFrom] = useState(new Date(new Date().setHours(0, 0, 0, 0)));
  const [to, setTo] = useState(new Date(new Date().setHours(23, 59, 59, 999)));

  const [isCalculating, setIsCalculating] = useState(false);
  const [stats, setStats] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [error, setError] = useState('');
  const [hasCalculated, setHasCalculated] = useState(false);

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoadingUsers(true);
      setUsersError('');
      const result = await getUsers();
      if (result.success) {
        setUsers(result.users || []);
      } else {
        setUsersError(result.error || 'Errore nel caricamento degli utenti');
      }
      setIsLoadingUsers(false);
    };

    if (user?.displayName === 'admin') {
      loadUsers();
    }
  }, [user]);

  const cashierUsers = useMemo(
    () => users.filter((u) => u.displayName === 'cashier'),
    [users]
  );

  const isDisabled = from > to || cashierUsers.length === 0 || isLoadingUsers;

  const calculateStats = async () => {
    if (!user) {
      setError('Utente non autenticato');
      return;
    }

    setIsCalculating(true);
    setError('');
    setStats([]);
    setHasCalculated(false);

    try {
      const cashierEmailsSet = new Set(
        cashierUsers.map((c) => c.email).filter(Boolean)
      );

      if (cashierEmailsSet.size === 0) {
        setHasCalculated(true);
        return;
      }

      const ordersQuery = query(
        collection(db, 'order'),
        where('createdAt', '>=', new Date(from)),
        where('createdAt', '<=', new Date(to))
      );

      const querySnapshot = await getDocs(ordersQuery);

      const totalsByCashier = {};
      const paymentTypeSet = new Set();

      querySnapshot.forEach((doc) => {
        const orderData = doc.data();
        const cashierEmail = orderData.cashierEmail;

        if (!cashierEmail || !cashierEmailsSet.has(cashierEmail)) {
          return;
        }

        const orderArray = orderData.order || [];
        const paymentType = orderData.paymentType || 'unknown';

        paymentTypeSet.add(paymentType);

        if (!totalsByCashier[cashierEmail]) {
          totalsByCashier[cashierEmail] = {
            byPaymentType: {},
            byPaymentTypeCount: {},
            total: 0
          };
        }

        if (!totalsByCashier[cashierEmail].byPaymentType[paymentType]) {
          totalsByCashier[cashierEmail].byPaymentType[paymentType] = 0;
        }
        if (!totalsByCashier[cashierEmail].byPaymentTypeCount[paymentType]) {
          totalsByCashier[cashierEmail].byPaymentTypeCount[paymentType] = 0;
        }

        let orderTotal = 0;
        orderArray.forEach((item) => {
          const itemTotal =
            (item.menuPrice || 0) * (item.quantity || 0) +
            (item.productPrice || 0) * (item.quantity || 0);
          orderTotal += itemTotal;
        });

        totalsByCashier[cashierEmail].byPaymentType[paymentType] += orderTotal;
        totalsByCashier[cashierEmail].byPaymentTypeCount[paymentType] += 1;
        totalsByCashier[cashierEmail].total += orderTotal;
      });

      const rows = Object.entries(totalsByCashier).map(
        ([cashierEmail, value]) => ({
          cashierEmail,
          byPaymentType: value.byPaymentType,
          byPaymentTypeCount: value.byPaymentTypeCount,
          total: value.total
        })
      );

      setStats(rows);
      setPaymentTypes(Array.from(paymentTypeSet));
      setHasCalculated(true);
    } catch (err) {
      console.error('Errore durante il calcolo delle statistiche casse:', err);
      setError('Errore durante il calcolo delle statistiche');
    } finally {
      setIsCalculating(false);
    }
  };

  if (loadingAuth) {
    return (
      <VStack w="100%" spacing={4} mt={10}>
        <Spinner size="xl" color="teal" />
        <Text color="white">Verifica autenticazione...</Text>
      </VStack>
    );
  }

  if (!user) {
    return <Navigate to="/dashboard" replace={true} />;
  }

  if (user.displayName !== 'admin') {
    return <Navigate to="/dashboard" replace={true} />;
  }

  const totalAllCashiers = stats.reduce(
    (sum, row) => sum + (row.total || 0),
    0
  );

  const totalsByPaymentTypeAllCashiers = paymentTypes.reduce(
    (acc, type) => {
      const sumForType = stats.reduce((sum, row) => {
        const value = row.byPaymentType?.[type] || 0;
        return sum + value;
      }, 0);
      acc[type] = sumForType;
      return acc;
    },
    {}
  );

  const ordersCountByPaymentTypeAllCashiers = paymentTypes.reduce(
    (acc, type) => {
      const sumForType = stats.reduce((sum, row) => {
        const value = row.byPaymentTypeCount?.[type] || 0;
        return sum + value;
      }, 0);
      acc[type] = sumForType;
      return acc;
    },
    {}
  );

  return (
    <VStack w="100%">
      <Heading mt={5} mb={0} color="white">
        Monitor Casse
      </Heading>

      <Text mb={3}>{process.env.REACT_APP_PROJECT_ID}</Text>
      <Button mb={3} as={Link} to="/dashboard">
        Dashboard
      </Button>

      <Box
        w="100%"
        border="3px solid teal"
        borderRadius={15}
        p={5}
        mb={10}
        color="white"
        bg="rgba(0, 0, 0, 0.3)"
      >
        {isLoadingUsers ? (
          <VStack spacing={4}>
            <Spinner size="xl" color="teal" />
            <Text>Caricamento utenti cassieri...</Text>
          </VStack>
        ) : usersError ? (
          <Alert status="error">
            <AlertIcon />
            {usersError}
          </Alert>
        ) : cashierUsers.length === 0 ? (
          <Text>Nessun utente con ruolo cashier trovato.</Text>
        ) : (
          <VStack align="stretch" spacing={4}>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="lg" fontWeight={600}>
                Dalla Data
              </Text>
              <DatePicker
                showTimeSelect
                dateFormat="MMMM d, yyyy H:mm"
                showPopperArrow={false}
                selected={from}
                onChange={(_date) => {
                  setFrom(_date);
                }}
                customInput={<CustomDatePicker />}
              />
              <Text fontSize="lg" fontWeight={600}>
                Alla Data
              </Text>
              <DatePicker
                showTimeSelect
                dateFormat="MMMM d, yyyy H:mm"
                showPopperArrow={false}
                selected={to}
                onChange={(_date) => {
                  setTo(_date);
                }}
                customInput={<CustomDatePicker />}
              />

              {hasCalculated && error && (
                <Text fontSize="lg" color="tomato">
                  {error}
                </Text>
              )}

              {hasCalculated && !error && stats.length === 0 && (
                <Text my={5}>
                  Nessuna statistica disponibile per questo periodo.
                </Text>
              )}
            </VStack>

            <HStack justify="flex-end">
              <Button
                minWidth={125}
                size="lg"
                isLoading={isCalculating}
                isDisabled={isDisabled}
                onClick={calculateStats}
              >
                Calcola
              </Button>
            </HStack>

            {hasCalculated && stats.length > 0 && (
              <TableContainer mt={5}>
                <Table variant="striped">
                  <Thead>
                    <Tr>
                      <Th>Cashier</Th>
                      {paymentTypes.map((type) => (
                        <Th key={type} isNumeric>
                          {type}
                        </Th>
                      ))}
                      <Th isNumeric>TOTALE</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {stats.map((row) => (
                      <Tr key={row.cashierEmail}>
                        <Td>{row.cashierEmail}</Td>
                        {paymentTypes.map((type) => {
                          const value = row.byPaymentType?.[type] || 0;
                          const count =
                            row.byPaymentTypeCount?.[type] || 0;
                          return (
                            <Td key={type} isNumeric>
                              € {value.toFixed(2)} ({count})
                            </Td>
                          );
                        })}
                        <Td isNumeric>€ {row.total.toFixed(2)}</Td>
                      </Tr>
                    ))}
                    <Tr fontWeight="bold">
                      <Td>TOTALE</Td>
                      {paymentTypes.map((type) => (
                        <Td key={type} isNumeric>
                          € {totalsByPaymentTypeAllCashiers[type].toFixed(2)} (
                          {ordersCountByPaymentTypeAllCashiers[type] || 0})
                        </Td>
                      ))}
                      <Td isNumeric>€ {totalAllCashiers.toFixed(2)}</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </VStack>
        )}
      </Box>

      <Footer />
    </VStack>
  );
}

export default CashierMonitor;

